/* eslint-disable radix */
/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import Typography from '@material-ui/core/Typography';
import CancelIcon from '@material-ui/icons/Cancel';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Select from '@material-ui/core/Select';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Box from '@material-ui/core/Box';
import LoadingPageText from '../../common/Loading/LoadingPageText';
import { useFetchActivityTypes } from '../../../actions/areas/admin/fetchHooks/fetchActivityType';
import {
  useFetchBandOptions,
  useFetchSkillsOptions,
  useFetchActivityVarianceOptions,
} from '../../../actions/areas/admin/fetchHooks/fetchActivity';
import { useFetchOrgUnitOptions } from './../../../actions/areas/shared/fetchOptions';
import { VisitType } from '../../../enums';
import { checkFeatureAccess } from '../../../utils/helpers';
import globals from '../../../globals/communityGlobals';

const errorsLocale = globals.getArea('error');

export default function ActivityForm({ props }) {
  const activity = props.activity;
  const [state, setState] = useState({
    id: activity.id ? activity.id : null,
    description: activity.description,
    selectedType: activity.activityTypeId,
    selectedBand: activity.bandId,
    selectedSkills: activity.skillIds,
    selectedTotalTime: activity.duration,
    selectedRagStatus: activity.ragId,
    selectedClinicianCount: activity.requiredClinicians,
    selectedVarianceFactorIds: activity.varianceFactors.map(v => v.id),
    selectedVarianceFactors: null,
    isActive: activity.id ? activity.status : true,
    isAssigned: activity.isAssigned,
    externalCode: activity.externalCode ? activity.externalCode : '',
    selectedOrgUnits: activity.orgUnitIds,
  });

  const faList = useSelector(store => store.global.auth.featureAccessList);
  const enableIntegrationMode = checkFeatureAccess(faList, globals.applicationFeatureAccesses.enableIntegrationMode);

  const { data: activityTypeOptions } = useFetchActivityTypes(props.visitType, false);
  const { data: bandOptions } = useFetchBandOptions(false);
  const { data: skillsOptions } = useFetchSkillsOptions(false);
  const { data: varianceFactorOptions } = useFetchActivityVarianceOptions(activity.id, false);
  const { data: orgUnitsOptions } = useFetchOrgUnitOptions(false);

  const mapVarianceFactors = () => {
    const values = varianceFactorOptions
      .filter(o => state.selectedVarianceFactorIds.find(v => v === o.id) !== undefined)
      .map(o => ({ id: o.id, value: o.name }));
    return values;
  };

  const locale = props.events.locale;
  const model = {
    ActivityId: state.id,
    ActivityTypeId: state.selectedType,
    BandId: state.selectedBand,
    RagStatusId: state.selectedRagStatus,
    Duration: parseInt(state.selectedTotalTime, 10),
    Name: state.description,
    Skills: state.selectedSkills,
    ActivityVarianceFactors: mapVarianceFactors(),
    RequiredClinicians: state.selectedClinicianCount,
    OrgUnits: state.selectedOrgUnits,
    TimeAverage: activity.timeAverage,
    ExternalCode: state.externalCode.length > 0 ? state.externalCode : null,
  };

  const updateStatusModel = {
    ActivityId: state.id,
    IsActive: state.isActive,
  };

  const canSave = () => {
    return (
      state.selectedType &&
      state.selectedBand &&
      state.description &&
      state.description.length <= 150 &&
      state.externalCode.length <= 150 &&
      state.selectedTotalTime &&
      state.selectedRagStatus &&
      state.selectedClinicianCount &&
      (parseInt(state.selectedTotalTime, 10) > 0 && parseInt(state.selectedTotalTime, 10) <= 1440)
    );
  };

  const headerTitle = state.id
    ? `Edit ${activity.description}`
    : `Create${props.visitType === 0 ? ' On Site ' : ' Phone Call Consultations '}Activity`;

  const validateFormData = () => {
    const errors = [];

    if (model.Duration <= 0 || model.Duration > 1440) {
      errors.push({ name: 'duration', message: 'The Total Activity Time must be between 1 and 1440.' });
    }

    model.ActivityVarianceFactors.forEach(aVariance => {
      if (parseInt(model.Duration) + parseInt(aVariance.value) <= 0) {
        errors.push({
          name: 'duration',
          message: `Activity variance with value ${aVariance.value} is equal or bigger than activity duration.`,
        });
      }
    });

    return {
      errors,
      isFormValid: errors.length === 0,
      isRequestValid: !(props.errorMsg && props.errorMsg.message.length > 0),
    };
  };

  const onSaveClick = () => {
    const formValidationError = validateFormData();
    if (formValidationError.isFormValid) {
      props.events.createOrEditActivity(model);
    }
  };

  const changeActiveStatus = () => {
    props.events.updateActiveStatus(updateStatusModel);
  };

  const errorPlaceholder = () => {
    const modelValidation = validateFormData();
    if (modelValidation.isFormValid && model.isRequestValid) return null;

    return (
      <span className="form-validation-error">
        {props.errorMsg ? <p>{props.errorMsg.message}</p> : null}
        {!modelValidation.isFormValid
          ? modelValidation.errors.map((item, i) => <p key={`${i}_${item.name}`}>{item.message}</p>)
          : null}
      </span>
    );
  };

  const headerRender = () => {
    return (
      <div className="sidePanel-header form-header">
        <div className="sidePanel-header-subtitle">
          <Box display="flex">
            <Box flexGrow={1} py={1}>
              <Typography variant="subtitle1">{headerTitle}</Typography>
            </Box>
            <Box>
              <IconButton onClick={() => props.events.onClose()}>
                <CancelIcon />
              </IconButton>
            </Box>
          </Box>
        </div>
      </div>
    );
  };

  const activityFormRender = () => {
    return (
      <div>
        {props.isDataLoading ? <LoadingPageText showHeader={false} /> : null}
        <form className="sidePanel-content content-top-padding" noValidate autoComplete="off" disabled="disabled">
          <Typography variant="h6" gutterBottom>
            Activity Details
          </Typography>
          {errorPlaceholder()}
          <div className={props.isDataLoading ? 'disable-form sidePanel-form' : 'sidePanel-form'}>
            <InputLabel id="activity-type">{locale.getMsg('activityTypeName')} *</InputLabel>
            <Select
              multiple={false}
              onChange={e => setState({ ...state, selectedType: e.target.value })}
              labelId="activity-type"
              value={state.selectedType}
            >
              {activityTypeOptions &&
                activityTypeOptions.map(type => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
            </Select>
            <InputLabel id="equivalence">{locale.getMsg('equivalence')} *</InputLabel>
            <Select
              multiple={false}
              onChange={e => setState({ ...state, selectedBand: e.target.value })}
              labelId="equivalence"
              value={state.selectedBand}
              autoWidth
            >
              {bandOptions &&
                bandOptions.map(band => (
                  <MenuItem key={band.id} value={band.id}>
                    {band.name}
                  </MenuItem>
                ))}
            </Select>
            <InputLabel id="description">{locale.getMsg('description')} *</InputLabel>
            <TextField
              required
              labelid="description"
              fullWidth
              onChange={e => setState({ ...state, description: e.target.value })}
              value={state.description}
              error={state.description.length > 150}
              helperText={state.description.length > 150 ? errorsLocale.getMsg('validationMaxLength', ['150']) : ''}
            />
            <InputLabel id="skills">{locale.getMsg('skills')}</InputLabel>
            <Select
              multiple
              onChange={e => setState({ ...state, selectedSkills: e.target.value })}
              labelId="skills"
              value={state.selectedSkills}
            >
              {skillsOptions &&
                skillsOptions.map(skill => (
                  <MenuItem key={skill.id} value={skill.id}>
                    {skill.name}
                  </MenuItem>
                ))}
            </Select>
            <InputLabel id="totalTime">{locale.getMsg('totalTime')} *</InputLabel>
            <TextField
              required
              labelid="totalTime"
              fullWidth
              type="number"
              InputProps={{ inputProps: { min: 1, max: 1440 } }}
              onChange={e => setState({ ...state, selectedTotalTime: e.target.value })}
              value={state.selectedTotalTime}
            />
            <InputLabel id="rag">{locale.getMsg('activityRiskRag')} *</InputLabel>
            <Select
              multiple={false}
              onChange={e => setState({ ...state, selectedRagStatus: e.target.value })}
              labelId="rag"
              value={state.selectedRagStatus}
              autoWidth
            >
              <MenuItem value={3}>{locale.getMsg('ragRed')}</MenuItem>
              <MenuItem value={2}>{locale.getMsg('ragAmber')}</MenuItem>
              <MenuItem value={4}>{locale.getMsg('ragYellow')}</MenuItem>
              <MenuItem value={1}>{locale.getMsg('ragGreen')}</MenuItem>
            </Select>
            <InputLabel id="clinician-count"> {locale.getMsg('requiredClinicians')} *</InputLabel>
            <Select
              multiple={false}
              onChange={e => setState({ ...state, selectedClinicianCount: e.target.value })}
              labelId="clinician-count"
              value={state.selectedClinicianCount}
              disabled={props.visitType === VisitType.PhoneCall}
              autoWidth
            >
              <MenuItem value={3}>3</MenuItem>
              <MenuItem value={2}>2</MenuItem>
              <MenuItem value={1}>1</MenuItem>
            </Select>
            <InputLabel id="activity-variance">{locale.getMsg('activityVarianceFactor')}</InputLabel>
            <Select
              multiple
              onChange={e => setState({ ...state, selectedVarianceFactorIds: e.target.value })}
              labelId="activity-variance"
              value={state.selectedVarianceFactorIds}
              autoWidth
            >
              {varianceFactorOptions &&
                varianceFactorOptions.map(varianceFactor => (
                  <MenuItem key={varianceFactor.id} value={varianceFactor.id}>
                    {varianceFactor.name}
                  </MenuItem>
                ))}
            </Select>
            {props.enableMappingOfActivities === 'true' && (
              <React.Fragment>
                <InputLabel id="map-to-teams">{locale.getMsg('mapToTeams')}</InputLabel>
                <Select
                  multiple
                  onChange={e => setState({ ...state, selectedOrgUnits: e.target.value })}
                  labelId="map-to-teams"
                  value={state.selectedOrgUnits}
                  autoWidth
                >
                  {orgUnitsOptions &&
                    orgUnitsOptions.map(orgUnit => (
                      <MenuItem key={orgUnit.id} value={orgUnit.id}>
                        {orgUnit.name}
                      </MenuItem>
                    ))}
                </Select>
              </React.Fragment>
            )}

            <InputLabel
              id="is-assigned"
              style={{ display: state.id ? 'block' : 'none', paddingBottom: '5px', fontWeight: 'bold' }}
            >
              {locale.getMsg('isAssigned')}
            </InputLabel>
            <TextField
              disabled
              labelid="is-assigned"
              fullWidth
              value={state.isAssigned ? state.isAssigned.toString() : ''}
              inputProps={{ style: { fontWeight: 'bold', color: 'black' } }}
            />
            {enableIntegrationMode && (
              <React.Fragment>
                <InputLabel id="externalCode">{locale.getMsg('externalCode')}</InputLabel>
                <TextField
                  labelid="externalCode"
                  fullWidth
                  onChange={e => setState({ ...state, externalCode: e.target.value })}
                  value={state.externalCode}
                  error={state.externalCode.length > 150}
                  helperText={
                    state.externalCode.length > 150 ? errorsLocale.getMsg('validationMaxLength', ['150']) : ''
                  }
                />
              </React.Fragment>
            )}
          </div>
        </form>
      </div>
    );
  };

  const footerRender = () => {
    return (
      <div className="sidePanel-footer">
        <div className="sidePanel-buttons">
          <div className="sidePanel-deactivateButton">
            <Button
              variant="contained"
              color={state.isActive ? 'secondary' : 'default'}
              disabled={state.isAssigned === 'Yes' || props.isDataLoading}
              onClick={changeActiveStatus}
              style={{ display: state.id ? '' : 'none', marginRight: '10px' }}
            >
              {state.isActive ? 'Deactivate' : 'Activate'}
            </Button>
          </div>
          <div className="sidePanel-saveButton">
            <Button
              variant="contained"
              color="primary"
              disabled={!canSave() || props.isDataLoading}
              onClick={onSaveClick}
            >
              {state.id ? 'Edit' : 'Create'}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="side-panel-content-wrapper">
      <SwipeableDrawer anchor="right" open onOpen={() => {}} onClose={() => {}}>
        <div className="sidePanel-wrapper" style={{ width: '450px' }}>
          {headerRender()}
          {activityFormRender()}
          {footerRender()}
        </div>
      </SwipeableDrawer>
    </section>
  );
}
